import { DateTime } from 'luxon';

import type { Timestep } from './predefinedRange';

export const getSmartDateLabel = (isoDate: string, hideOn?: boolean) => {
  if (!isoDate) {
    return '-';
  }

  const today = DateTime.local().startOf('day');
  const txnDate = DateTime.fromISO(isoDate).startOf('day');
  const dayDiff = txnDate.diff(today, 'days').days;

  let dayString = 'someday';
  const absDiff = Math.abs(dayDiff);
  if (isNaN(dayDiff)) {
    assert(false, `Invalid date passed to getSmartDateLabel: '${isoDate}'`);
  } else {
    switch (dayDiff) {
      case 0:
        dayString = 'today';
        break;
      case -1:
        dayString = 'yesterday';
        break;
      case 1:
        dayString = 'tomorrow';
        break;
      default:
        if (absDiff > 6) {
          // Decision was made to always show the year - see comment in SIMPL-19941
          // if (txnDate.year === today.year) {
          //   dayString = `on ${txnDate.toFormat('LLL d')}`;
          // } else {
          dayString = `${hideOn ? '' : 'on '}${txnDate.toFormat('LLL d, yyyy')}`;
          // }
        } else {
          dayString = dayDiff > 0 ? `In ${dayDiff} days` : `${absDiff} days ago`;
        }
        break;
    }
  }

  return dayString;
};

export const getShortMonthLabelFromISO = (date: string) => DateTime.fromISO(date).toLocaleString({ month: 'short' });

export const getReadableDateRange = (timeStep: Timestep, date1: string, date2: string, showYear?: boolean) => {
  const year = DateTime.fromISO(date1).year;
  switch (timeStep) {
    case 'DAY':
      return showYear === false
        ? DateTime.fromISO(date1).toFormat('MMM d')
        : DateTime.fromISO(date1).toFormat('MMM d, yyyy');
    case 'MONTH': {
      const label = getShortMonthLabelFromISO(date1);
      return showYear ? `${label} ${year}` : label;
    }
    case 'WEEK': {
      const label = `${DateTime.fromISO(date1).toFormat('MMM d')} - ${DateTime.fromISO(date2).toFormat('MMM d')}`;
      return showYear ? `${label} ${year}` : label;
    }
    case 'QUARTER': {
      const label = `${getShortMonthLabelFromISO(date1)} - ${getShortMonthLabelFromISO(date2)}`;
      return showYear ? `${label} ${year}` : label;
    }
    case 'YEAR':
      return String(year);
    default:
      return `${date1} - ${date2}`;
  }
};

/**
 * Format a date range with the year on both ends, so a range crossing a year boundary reads
 * unambiguously. `getReadableDateRange` can't do this — it only ever renders `date1`'s year.
 * @param startISO - ISO date string (e.g. "2025-11-20")
 * @param endISO - ISO date string (e.g. "2026-01-01")
 * @returns formatted string: "Nov 20, 2025 - Jan 1, 2026"
 */
export const getFullDateRange = (startISO: string, endISO: string): string => {
  if (!startISO || !endISO) return '';
  const start = DateTime.fromISO(startISO);
  const end = DateTime.fromISO(endISO);
  if (!start.isValid || !end.isValid) return '';
  return `${start.toFormat('LLL d, yyyy')} - ${end.toFormat('LLL d, yyyy')}`;
};
